const DB_NAME = "icakene-offline";
const DB_VERSION = 2;
const UNASSIGNED = "__legacy__";
export const CACHE_STORE = "responses";
export const OUTBOX_STORE = "outbox";
export interface CachedResponse { key: string; scope: string; url: string; body: unknown; cachedAt: number; }
export interface OutboxEntry { id?: number; scope: string; method: string; url: string; body: unknown; label: string; createdAt: number; attempts: number; error?: string; }
let dbPromise: Promise<IDBDatabase> | null = null;
function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("IndexedDB indisponible")); return; }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (db.objectStoreNames.contains(CACHE_STORE)) db.deleteObjectStore(CACHE_STORE);
      db.createObjectStore(CACHE_STORE, { keyPath: "key" });
      const outbox = db.objectStoreNames.contains(OUTBOX_STORE)
        ? request.transaction!.objectStore(OUTBOX_STORE)
        : db.createObjectStore(OUTBOX_STORE, { keyPath: "id", autoIncrement: true });
      if (!outbox.indexNames.contains("scope")) outbox.createIndex("scope", "scope");
      const cursor = outbox.openCursor();
      cursor.onsuccess = () => {
        const row = cursor.result;
        if (!row) return;
        if (!row.value.scope) row.update({ ...row.value, scope: UNASSIGNED, error: "Ancienne modification sans propriétaire vérifiable. Reprise manuelle requise." });
        row.continue();
      };
    };
    request.onsuccess = () => { request.result.onversionchange = () => request.result.close(); resolve(request.result); };
    request.onerror = () => { dbPromise = null; reject(request.error); };
  });
  return dbPromise;
}
async function run<T>(store: string, mode: IDBTransactionMode, work: (s: IDBObjectStore) => IDBRequest): Promise<T> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, mode);
    const request = work(tx.objectStore(store));
    let value: T;
    request.onsuccess = () => { value = request.result as T; };
    tx.oncomplete = () => resolve(value);
    tx.onerror = () => reject(tx.error ?? request.error);
    tx.onabort = () => reject(tx.error ?? new Error("Transaction interrompue"));
  });
}
async function ownedWrite(id: number, scope: string, replacement?: OutboxEntry): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(OUTBOX_STORE, "readwrite");
    const store = tx.objectStore(OUTBOX_STORE);
    const request = store.get(id);
    request.onsuccess = () => {
      if (request.result?.scope !== scope || scope === UNASSIGNED) { tx.abort(); return; }
      if (replacement) store.put(replacement); else store.delete(id);
    };
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(new Error("Modification hors de la session active"));
    tx.onerror = () => reject(tx.error);
  });
}
export const offlineDb = {
  putCached(url: string, body: unknown, scope: string): Promise<unknown> {
    if (!scope) return Promise.reject(new Error("Session requise"));
    return run(CACHE_STORE, "readwrite", s => s.put({ key: JSON.stringify([scope, url]), scope, url, body, cachedAt: Date.now() } satisfies CachedResponse));
  },
  async getCached(url: string, scope: string): Promise<CachedResponse | undefined> {
    if (!scope) return Promise.resolve(undefined);
    const key = JSON.stringify([scope, url]);
    const cached = await run<CachedResponse | undefined>(CACHE_STORE, "readonly", s => s.get(key));
    if (cached && Date.now() - cached.cachedAt > 24 * 60 * 60 * 1000) {
      await run(CACHE_STORE, "readwrite", s => s.delete(key));
      return undefined;
    }
    return cached;
  },
  clearCache(): Promise<unknown> { return run(CACHE_STORE, "readwrite", s => s.clear()); },
  enqueue(entry: Omit<OutboxEntry, "id">): Promise<number> {
    if (!entry.scope || entry.scope === UNASSIGNED) return Promise.reject(new Error("Session requise"));
    return run(OUTBOX_STORE, "readwrite", s => s.add(entry));
  },
  listOutbox(scope: string): Promise<OutboxEntry[]> {
    if (!scope || scope === UNASSIGNED) return Promise.resolve([]);
    return run(OUTBOX_STORE, "readonly", s => s.index("scope").getAll(scope));
  },
  countUnassigned(): Promise<number> { return run(OUTBOX_STORE, "readonly", s => s.index("scope").count(UNASSIGNED)); },
  updateEntry(entry: OutboxEntry): Promise<void> { return ownedWrite(entry.id!, entry.scope, entry); },
  removeEntry(id: number, scope: string): Promise<void> { return ownedWrite(id, scope); },
};
